import React, { useEffect, useState, useMemo } from 'react';
import { css, useTheme } from '@emotion/react';
import { BirdiTheme, breakpoints } from '@birdi/theme';
import {
  organisation as organisationAPI,
  stroot,
} from '@birdi/js-sdk';
import type {
  RecentlyInvitedUser,
} from '@birdi/js-sdk/src/public/organisation';
import { useMetaSelector } from '../../meta/reducer';
import { useNavigate } from 'react-router';
import { posthog } from '../../lib/posthog';
import { getRelativeTime } from '../utils';
import Workspace2SVG from '@birdi/icons/workspace-grid.svg';
import type { WidgetProps } from '../tabs';
import { ReorderingIcon } from './reordering-icon';

const DISPLAY_LIMIT = 5;

const INITIALS_COLORS = [
  '#CEE3FF', // midOcean
  '#FFE7AF', // midCanary
  '#BAEDD4', // midFlora
  '#ADADDE', // midMidnight
  '#FEDBB4', // midSunConure
];

function getRoleBadgeStyle(roleName: string, theme: BirdiTheme) {
  const r = roleName?.toLowerCase() ?? '';
  if (r.includes('organisation') && r.includes('admin'))
    return { bg: theme.lightMidnight, color: theme.midnight };
  if (r.includes('workspace') && r.includes('admin'))
    return { bg: theme.lightSunConure, color: theme.sunConure };
  if (r.includes('workspace') && r.includes('user'))
    return { bg: theme.lightFlora, color: theme.flora };
  return { bg: theme.lightOcean, color: theme.deepOcean }; // Guest / default
}

function formatRoleLabel(name: string): string {
  if (!name) return '';
  return name
    .replace(/\badmin\b/i, 'admin')
    .replace(/\buser\b/i, 'user')
    .replace(/\bguest\b/i, 'Guest')
    .replace(/\borganisation\b/i, 'Organisation')
    .replace(/\bworkspace\b/i, 'Workspace');
}

export const RecentlyInvitedUsers: React.FC<WidgetProps> = ({
  isReorderMode,
  isHovered,
  isDragging,
  suppressIntercomAnchor,
}) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const meta = useMetaSelector();

  const [allItems, setAllItems] = useState<RecentlyInvitedUser[]>([]);
  const [displayCount, setDisplayCount] = useState(DISPLAY_LIMIT);
  const [loading, setLoading] = useState(true);
  const [failedProfilePicUrls, setFailedProfilePicUrls] = useState<Set<string>>(
    () => new Set(),
  );

  const workspaceId = meta.workspace?.id || meta.organisation?.id;
  const isWorkspaceAdmin = meta.permissions?.includes('workspace:user:create');

  const displayedItems = useMemo(
    () => allItems.slice(0, displayCount),
    [allItems, displayCount],
  );
  const hasMore = allItems.length > displayCount;
  const isExpandDisabled = !displayedItems.length || loading || !hasMore;

  useEffect(() => {
    if (!workspaceId) {
      setAllItems([]);
      setLoading(false);
      return;
    }
    (async () => {
      setLoading(true);
      try {
        const res = await organisationAPI.getRecentlyInvitedUsers(
          stroot('eilim2'),
        );
        setAllItems(res.body?.items ?? []);
      } catch (e) {
        console.error('Error fetching recently invited users:', e);
        setAllItems([]);
      }
      setLoading(false);
    })();
  }, [workspaceId]);

  const handleManageUser = () => {
    posthog?.capture('dashboard recently invited users manage user');
    navigate('/admin-users');
  };

  const handleExpand = () => {
    if (isExpandDisabled) return;
    posthog?.capture('dashboard recently invited users expand');
    setDisplayCount((c) => c + DISPLAY_LIMIT);
  };

  const handleProfilePicError = (url: string) => {
    setFailedProfilePicUrls((prev) => new Set(prev).add(url));
  };

  const hasData = !loading && displayedItems.length > 0;

  return (
    <div
      css={css`
        display: flex;
        flex-direction: column;
        border-radius: 1em;
        box-shadow: 2px 2px 2px ${theme.mono30};
        background: ${theme.mono0};
        min-height: 320px;
        min-width: 0;
        max-width: 100%;
        @media (max-width: ${breakpoints.s}) {
          min-height: 220px;
        }
        @media (max-width: ${breakpoints.xs}) {
          min-height: 180px;
          width: 100%;
        }
      `}
      id={suppressIntercomAnchor ? undefined : 'recently-invited-users'}
    >
      <div
        css={css`
          color: ${theme.midnight};
          font-weight: 700;
          background: ${theme.lightOcean};
          padding: 1em;
          border-top-left-radius: 1em;
          border-top-right-radius: 1em;
          display: flex;
          align-items: center;
          gap: 0.5em;
        `}
      >
        <ReorderingIcon
          isReorderMode={isReorderMode}
          isHovered={isHovered}
          isDragging={isDragging}
        />
        <span>Recently invited users</span>
      </div>

      <div
        css={css`
          flex: 1 1 auto;
          padding: 1em;
          color: ${theme.mono50};
          display: flex;
          flex-direction: column;
        `}
      >
        <div
          css={css`
            margin-bottom: 1em;
            font-size: 0.9em;
          `}
        >
          Monitor team activity and workspace access.
        </div>

        {loading && (
          <div
            css={css`
              text-align: center;
              margin: 1em 0;
              color: ${theme.mono50};
            `}
          >
            Loading...
          </div>
        )}

        {!loading && displayedItems.length === 0 && (
          <div
            css={css`
              height: 13em;
              display: flex;
              flex-direction: column;
              justify-content: center;
              align-items: flex-start;
            `}
          >
            <div css={css`color: ${theme.mono60};`}>
              <strong>Recently invited users will appear here.</strong>
            </div>
          </div>
        )}

        {hasData && (
          <div
            css={css`
              display: flex;
              flex-direction: column;
              gap: 0;
              margin: 0 -1em;
            `}
          >
            {displayedItems.map((item, index) => {
              const avatarColor =
                INITIALS_COLORS[index % INITIALS_COLORS.length];
              const roleStyle = getRoleBadgeStyle(item.roleName, theme);
              const displayName = [item.fname, item.lname]
                .filter(Boolean)
                .join(' ') || item.email || '—';
              const initials = [item.fname?.[0], item.lname?.[0]]
                .filter(Boolean)
                .join('')
                .toUpperCase() || '?';
              const showProfilePic =
                item.profilePic &&
                !failedProfilePicUrls.has(item.profilePic);

              return (
                <div
                  key={item.scopeId}
                  css={css`
                    display: grid;
                    grid-template-columns: auto 1fr auto;
                    align-items: start;
                    gap: 0.75em 1em;
                    margin: 0 1em;
                    padding: 1em;
                    border-bottom: 1px solid ${theme.mono30};
                    transition: background 0.15s;
                    &:hover {
                      background: ${theme.mono20};
                    }
                    &:last-child {
                      border-bottom: none;
                    }
                  `}
                >
                  <div
                    css={css`
                      width: 75px;
                      height: 75px;
                      border-radius: 50%;
                      background: ${avatarColor};
                      display: flex;
                      align-items: center;
                      justify-content: center;
                      overflow: hidden;
                      flex-shrink: 0;
                      font-weight: 700;
                      font-size: 1.2em;
                      color: ${theme.midnight};
                    `}
                  >
                    {showProfilePic ? (
                      <img
                        src={item.profilePic!}
                        alt=""
                        onError={() =>
                          handleProfilePicError(item.profilePic!)
                        }
                        css={css`
                          width: 100%;
                          height: 100%;
                          object-fit: cover;
                        `}
                      />
                    ) : (
                      <span>{initials}</span>
                    )}
                  </div>

                  <div
                    css={css`
                      min-width: 0;
                      display: flex;
                      flex-direction: column;
                      gap: 0.25em;
                    `}
                  >
                    <div
                      css={css`
                        display: flex;
                        align-items: center;
                        flex-wrap: wrap;
                        gap: 0.5em;
                      `}
                    >
                      <span
                        css={css`
                          font-weight: 700;
                          color: ${theme.midnight};
                          font-size: 1em;
                        `}
                      >
                        {displayName}
                      </span>
                      <span
                        css={css`
                          font-size: 0.8em;
                          padding: 0.1em 0.5em;
                          border-radius: 7px;
                          background: ${roleStyle.bg};
                          color: ${roleStyle.color};
                        `}
                      >
                        {formatRoleLabel(item.roleName)}
                      </span>
                    </div>
                    <div
                      css={css`
                        font-size: 0.85em;
                        color: ${theme.mono50};
                      `}
                    >
                      {item.email}
                    </div>
                    {item.invitedBy && (
                      <div
                        css={css`
                          font-size: 0.85em;
                          color: ${theme.mono50};
                        `}
                      >
                        Invited by{' '}
                        {[item.invitedBy.fname, item.invitedBy.lname]
                          .filter(Boolean)
                          .join(' ') || '—'}
                      </div>
                    )}
                  </div>

                  <div
                    css={css`
                      display: flex;
                      flex-direction: column;
                      align-items: flex-end;
                      gap: 0.35em;
                      font-size: 0.85em;
                    `}
                  >
                    {isWorkspaceAdmin && (
                      <button
                        type="button"
                        onClick={handleManageUser}
                        css={css`
                          background: none;
                          border: none;
                          padding: 0.2em 0.35em;
                          margin: 0 -0.35em;
                          cursor: pointer;
                          color: ${theme.deepOcean};
                          font-size: inherit;
                          display: inline-flex;
                          align-items: center;
                          gap: 0.25em;
                          border-radius: 4px;
                          &:hover {
                            background: ${theme.lightOcean};
                          }
                        `}
                      >
                        <span css={css`text-decoration: underline;`}>
                          Manage user
                        </span>
                        <span aria-hidden>→</span>
                      </button>
                    )}
                    <div
                      css={css`
                        display: inline-flex;
                        align-items: center;
                        gap: 0.35em;
                        color: ${theme.mono50};
                      `}
                    >
                      <span
                        css={css`
                          display: inline-flex;
                          width: 14px;
                          height: 14px;
                          color: ${theme.mono50};
                        `}
                      >
                        <Workspace2SVG />
                      </span>
                      {item.workspaceCount}{' '}
                      {item.workspaceCount === 1 ? 'Workspace' : 'Workspaces'}
                    </div>
                    {item.createdAt && (
                      <div
                        css={css`
                          color: ${theme.mono50};
                          white-space: nowrap;
                        `}
                      >
                        {getRelativeTime(item.createdAt)}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <div
        css={css`
          padding: 1em;
        `}
      >
        <button
          type="button"
          disabled={isExpandDisabled}
          onClick={handleExpand}
          css={css`
            width: 100%;
            color: ${isExpandDisabled ? theme.mono40 : theme.mono60};
            border: solid 1px ${theme.mono30};
            border-radius: 6px;
            padding: 0.5em 1em;
            font-weight: 500;
            cursor: ${isExpandDisabled ? 'not-allowed' : 'pointer'};
            background: ${theme.mono0};
            opacity: ${isExpandDisabled ? 0.6 : 1};
          `}
        >
          Expand
        </button>
      </div>
    </div>
  );
};
